import { useState, useCallback, useRef } from "react"
import { keys } from "../../../keys"
import type { GlobalAIMessage, ToolCall, GlobalAIStreamEvent, MatterCardsData } from "../types"

interface UseGlobalAIStreamOptions {
  onSessionNameUpdate?: (name: string) => void
  onMatterCards?: (data: MatterCardsData) => void
}

interface UseGlobalAIStreamReturn {
  messages: GlobalAIMessage[]
  isStreaming: boolean
  currentText: string
  currentToolCalls: ToolCall[]
  matterCards: MatterCardsData | null
  error: string | null
  sendMessage: (sessionId: string, content: string) => Promise<void>
  setMessages: React.Dispatch<React.SetStateAction<GlobalAIMessage[]>>
  cancelStream: () => void
  clearMatterCards: () => void
  restoreMatterCards: (data: MatterCardsData | null) => void
}

export function useGlobalAIStream(options?: UseGlobalAIStreamOptions): UseGlobalAIStreamReturn {
  const [messages, setMessages] = useState<GlobalAIMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [currentText, setCurrentText] = useState("")
  const [currentToolCalls, setCurrentToolCalls] = useState<ToolCall[]>([])
  const [matterCards, setMatterCards] = useState<MatterCardsData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const currentModelRef = useRef<string | undefined>(undefined)
  const currentTextRef = useRef<string>("")
  const currentToolCallsRef = useRef<ToolCall[]>([])

  const sendMessage = useCallback(
    async (sessionId: string, content: string): Promise<void> => {
      // Cancel any existing stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      setIsStreaming(true)
      setCurrentText("")
      setCurrentToolCalls([])
      setError(null)
      // Don't clear matter cards here - they persist until new cards arrive or session changes

      // Reset refs for new message
      currentTextRef.current = ""
      currentToolCallsRef.current = []
      currentModelRef.current = undefined

      // Add user message immediately
      const userMessage: GlobalAIMessage = {
        role: "user",
        content,
        timestamp: Math.floor(Date.now() / 1000),
        tool_calls: [],
      }
      setMessages((prev) => [...prev, userMessage])

      abortControllerRef.current = new AbortController()

      try {
        // Use the global streaming endpoint
        const response = await fetch(`${keys.API_URL}/v1/ai_agent/sessions/${sessionId}/stream/global`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${keys.SYSTEM_API_KEY}`,
          },
          credentials: "include",
          body: JSON.stringify({ content }),
          signal: abortControllerRef.current.signal,
        })

        if (!response.ok) {
          throw new Error("Failed to connect to Global AI")
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error("No response stream available")
        }

        const decoder = new TextDecoder()
        let buffer = ""
        let accumulatedText = ""
        let currentModel: string | undefined = undefined
        const accumulatedToolCalls: ToolCall[] = []

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split("\n")
          buffer = lines.pop() || ""

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const event: GlobalAIStreamEvent = JSON.parse(line.slice(6))

                switch (event.type) {
                  case "thinking":
                    // Show thinking indicator (handled by isStreaming)
                    break

                  case "tool_call":
                    if (event.name) {
                      const toolCall: ToolCall = {
                        name: event.name,
                        input: event.input || {},
                        output_summary: "",
                      }
                      accumulatedToolCalls.push(toolCall)
                      currentToolCallsRef.current = [...accumulatedToolCalls]
                      setCurrentToolCalls([...accumulatedToolCalls])
                    }
                    break

                  case "tool_result":
                    if (event.name && event.summary) {
                      // Update the last tool call with the summary
                      const lastIndex = accumulatedToolCalls.findIndex(
                        (tc) => tc.name === event.name && !tc.output_summary
                      )
                      if (lastIndex >= 0) {
                        accumulatedToolCalls[lastIndex].output_summary = event.summary
                        currentToolCallsRef.current = [...accumulatedToolCalls]
                        setCurrentToolCalls([...accumulatedToolCalls])
                      }
                    }
                    break

                  case "text_delta":
                    if (event.content) {
                      accumulatedText += event.content
                      currentTextRef.current = accumulatedText
                      setCurrentText(accumulatedText)
                    }
                    break

                  case "matter_cards":
                    // Handle matter cards from return_matter_cards tool
                    if (event.data) {
                      setMatterCards(event.data)
                      options?.onMatterCards?.(event.data)
                    }
                    break

                  case "done":
                    // Finalize the assistant message
                    const assistantMessage: GlobalAIMessage = {
                      role: "assistant",
                      content: accumulatedText,
                      timestamp: Math.floor(Date.now() / 1000),
                      model_used: currentModel,
                      tool_calls: accumulatedToolCalls,
                    }
                    setMessages((prev) => [...prev, assistantMessage])
                    setIsStreaming(false)
                    setCurrentText("")
                    setCurrentToolCalls([])
                    break

                  case "error":
                    setError(event.message || "An error occurred")
                    setIsStreaming(false)
                    break

                  case "session_name":
                    if (event.name && options?.onSessionNameUpdate) {
                      options.onSessionNameUpdate(event.name)
                    }
                    break

                  case "model_selected":
                    if (event.model) {
                      currentModel = event.model
                      currentModelRef.current = event.model
                    }
                    break
                }
              } catch (parseError) {
                console.error("Failed to parse SSE event:", parseError)
              }
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setError((err as Error).message || "Failed to send message")
          setIsStreaming(false)
        }
      }
    },
    [options]
  )

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    // Keep partial response as a message if there's any content
    const text = currentTextRef.current
    const toolCalls = currentToolCallsRef.current

    if (text || toolCalls.length > 0) {
      const partialMessage: GlobalAIMessage = {
        role: "assistant",
        content: text,
        timestamp: Math.floor(Date.now() / 1000),
        model_used: currentModelRef.current,
        tool_calls: toolCalls,
      }
      setMessages((prev) => [...prev, partialMessage])
    }

    // Reset state
    setIsStreaming(false)
    setCurrentText("")
    setCurrentToolCalls([])

    // Reset refs
    currentTextRef.current = ""
    currentToolCallsRef.current = []
    currentModelRef.current = undefined
  }, [])

  const clearMatterCards = useCallback(() => {
    setMatterCards(null)
  }, [])

  const restoreMatterCards = useCallback((data: MatterCardsData | null) => {
    setMatterCards(data)
  }, [])

  return {
    messages,
    isStreaming,
    currentText,
    currentToolCalls,
    matterCards,
    error,
    sendMessage,
    setMessages,
    cancelStream,
    clearMatterCards,
    restoreMatterCards,
  }
}
